import { Star, Bed, Bath, Users, Heart } from 'lucide-react'
import { IMAGE_CONFIG } from '../../config/settings'

interface RoomCardProps {
  room: {
    id: number | string
    title: string
    roomNumber: string
    provider: string
    price: number
    rating: number
    beds: number
    baths: number
    guests: number
    image: string
    amenities: (string | { name?: string })[]
    summary?: string
  }
  viewMode: 'grid' | 'list'
  checkIn?: string
  checkOut?: string
  guests?: string
}

const RoomCard = ({ room, viewMode, checkIn, checkOut, guests }: RoomCardProps) => {
  const handleCardClick = () => {
    window.location.href = `/room/${room.id}?checkin=${checkIn || ''}&checkout=${checkOut || ''}&guests=${guests || ''}`
  }

  const handleBookNow = (e: React.MouseEvent) => {
    e.stopPropagation()
    handleCardClick()
  }

  if (viewMode === 'grid') {
    return (
      <div 
        className="bg-white rounded-2xl shadow-md hover:shadow-xl transition-all duration-300 overflow-hidden group cursor-pointer"
        onClick={handleCardClick}
      >
        {/* Image */}
        <div className="relative h-48 sm:h-56 overflow-hidden">
          <img
            src={room.image}
            alt={room.title}
            className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
            onError={(e) => {
              e.currentTarget.src = IMAGE_CONFIG.FALLBACK_IMAGE
            }}
          />
          <div className="absolute top-3 right-3 bg-black/70 backdrop-blur-sm text-white px-3 py-1.5 rounded-lg text-sm font-semibold">
            ₹{room.price}/night
          </div>
          <div className="absolute top-3 left-3 bg-white/90 backdrop-blur-sm rounded-lg px-2 py-1.5">
            <div className="flex items-center gap-1">
              {[...Array(5)].map((_, i) => (
                <Star
                  key={i}
                  className={`h-3.5 w-3.5 ${
                    i < Math.floor(room.rating)
                      ? 'text-yellow-400 fill-current'
                      : 'text-gray-300'
                  }`}
                />
              ))}
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="p-4 sm:p-5">
          <h3 className="text-lg sm:text-xl font-bold text-gray-900 mb-2 line-clamp-1">
            {room.title}
          </h3>
          <p className="text-primary-600 font-medium text-sm mb-2">
            By {room.provider}
          </p>
          {room.summary && (
            <p className="text-gray-600 text-sm mb-4 line-clamp-2">
              {room.summary}
            </p>
          )}

          {/* Features */}
          <div className="flex items-center gap-4 mb-4 pb-4 border-b border-gray-100">
            <div className="flex items-center gap-1.5 text-gray-600">
              <Bed className="h-4 w-4" />
              <span className="text-sm font-medium">{room.beds}</span>
            </div>
            <div className="flex items-center gap-1.5 text-gray-600">
              <Bath className="h-4 w-4" />
              <span className="text-sm font-medium">{room.baths}</span>
            </div>
            <div className="flex items-center gap-1.5 text-gray-600">
              <Users className="h-4 w-4" />
              <span className="text-sm font-medium">{room.guests}</span>
            </div>
          </div>

          {/* Amenities */}
          {room.amenities && room.amenities.length > 0 && (
            <div className="flex flex-wrap gap-1.5 mb-4">
              {room.amenities.slice(0, 3).map((amenity, index) => (
                <span
                  key={index}
                  className="px-2.5 py-1 bg-primary-50 text-primary-700 rounded-full text-xs font-medium"
                >
                  {typeof amenity === 'string' 
                    ? amenity.charAt(0).toUpperCase() + amenity.slice(1)
                    : amenity.name || String(amenity)
                  }
                </span>
              ))}
              {room.amenities.length > 3 && (
                <span className="px-2.5 py-1 bg-gray-100 text-gray-600 rounded-full text-xs font-medium">
                  +{room.amenities.length - 3}
                </span>
              )}
            </div>
          )}

          {/* Action Button */}
          <button 
            onClick={handleBookNow}
            className="w-full bg-primary-500 text-white px-4 py-3 rounded-lg font-semibold hover:bg-primary-600 transition-colors text-sm shadow-md"
          >
            Book Now
          </button>
        </div>
      </div>
    )
  }

  // List View
  return (
    <div 
      className="bg-white rounded-2xl shadow-md hover:shadow-xl transition-all duration-300 overflow-hidden group cursor-pointer"
      onClick={handleCardClick}
    >
      <div className="flex flex-col sm:flex-row">
        {/* Image */}
        <div className="sm:w-80 relative h-64 sm:h-auto min-h-[256px]">
          <img
            src={room.image}
            alt={room.title}
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
            onError={(e) => {
              e.currentTarget.src = IMAGE_CONFIG.FALLBACK_IMAGE
            }}
          />
          <div className="absolute top-4 right-4 bg-black/70 backdrop-blur-sm text-white px-3 py-1.5 rounded-lg text-sm font-semibold">
            ₹{room.price}/night
          </div>
          <div className="absolute top-4 left-4 bg-white/90 backdrop-blur-sm rounded-lg px-2 py-1.5">
            <div className="flex items-center gap-1">
              {[...Array(5)].map((_, i) => (
                <Star
                  key={i}
                  className={`h-3.5 w-3.5 ${
                    i < Math.floor(room.rating)
                      ? 'text-yellow-400 fill-current'
                      : 'text-gray-300'
                  }`}
                />
              ))}
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 p-4 sm:p-6 flex flex-col">
          <div className="flex-1">
            <h3 className="text-xl sm:text-2xl font-bold text-gray-900 mb-2">
              {room.title}
            </h3>
            <p className="text-primary-600 font-medium mb-2">
              By {room.provider} • Room {room.roomNumber}
            </p>
            {room.summary && (
              <p className="text-gray-600 text-sm mb-4 line-clamp-2">
                {room.summary}
              </p>
            )}

            {/* Features */}
            <div className="flex items-center gap-4 sm:gap-6 mb-4 flex-wrap">
              <div className="flex items-center gap-2 text-gray-600">
                <Bed className="h-5 w-5" />
                <span className="font-medium text-sm">{room.beds} Beds</span>
              </div>
              <div className="flex items-center gap-2 text-gray-600">
                <Bath className="h-5 w-5" />
                <span className="font-medium text-sm">{room.baths} Baths</span>
              </div>
              <div className="flex items-center gap-2 text-gray-600">
                <Users className="h-5 w-5" />
                <span className="font-medium text-sm">{room.guests} Guests</span>
              </div>
            </div>

            {/* Amenities */}
            {room.amenities && room.amenities.length > 0 && (
              <div className="flex flex-wrap gap-2 mb-4">
                {room.amenities.map((amenity, index) => (
                  <span
                    key={index}
                    className="px-3 py-1 bg-primary-50 text-primary-700 rounded-full text-xs font-medium"
                  >
                    {typeof amenity === 'string' 
                      ? amenity.charAt(0).toUpperCase() + amenity.slice(1)
                      : amenity.name || String(amenity)
                    }
                  </span>
                ))}
              </div>
            )}
          </div>

          {/* Action Buttons */}
          <div className="flex items-center gap-3 pt-4 border-t border-gray-100">
            <div className="flex-1">
              <div className="text-2xl font-bold text-primary-500">₹{room.price}</div>
              <div className="text-xs text-gray-500">per night</div>
            </div>
            <button 
              onClick={handleBookNow}
              className="px-6 py-3 bg-primary-500 text-white rounded-lg font-semibold hover:bg-primary-600 transition-colors text-sm shadow-md"
            >
              Book Now
            </button>
            <button 
              onClick={(e) => e.stopPropagation()}
              className="p-3 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
            >
              <Heart className="h-5 w-5 text-gray-600" />
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}

export default RoomCard

